import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Dialog,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import api from '../../utils/api';

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  address?: string;
  qualifications?: string;
  modality: string[];
  sessionTypes: string[];
  availability: string[];
  activeClients?: string[];
  application?: {
    counselling?: {
      experience?: string;
    };
  };
}

interface Lookup {
  value: string;
  displayName: string;
}

interface ProfileEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name: string, selected: readonly string[], theme: Theme) {
  return {
    fontWeight:
      selected.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  borderBottom: '1px solid',
  borderColor: theme.palette.divider,
  padding: theme.spacing(2, 3),
  '& .MuiTypography-root': {
    fontFamily: 'Roboto',
    fontWeight: 500,
    fontSize: '1.25rem',
  },
}));

const ProfileEdit: React.FC<ProfileEditProps> = ({ open, onClose, onSaved }) => {
  const theme = useTheme();
  const [counsellor, setCounsellor] = useState<Counsellor>({
    _id: '',
    firstName: '',
    lastName: '',
    phone: '',
    modality: [],
    sessionTypes: [],
    availability: []
  });
  const [modalityOptions, setModalityOptions] = useState<Lookup[]>([]);
  const [sessionTypesOptions, setSessionTypesOptions] = useState<Lookup[]>([]);
  const [availabilityOptions, setAvailabilityOptions] = useState<Lookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sourced from application.counselling.experience - the free-text
  // narrative displayed on the Counsellor Details page.
  const [experience, setExperience] = useState('');

  useEffect(() => {
    if (open) {
      fetchCounsellorData();
      fetchLookupData();
    }
  }, [open]);

  const fetchCounsellorData = async () => {
    try {
      const response = await api.get('/api/counsellors/profile');
      setCounsellor(response.data);
      setExperience(response.data.application?.counselling?.experience || '');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching counsellor data:', err);
      setError('Failed to load profile');
      setLoading(false);
    }
  };


  const fetchLookupData = async () => {
    try {
      const [modality, sessionTypes, availability] = await Promise.all([
        api.get('/api/lookups/modality'),
        api.get('/api/lookups/sessionTypes'),
        api.get('/api/lookups/availability')
      ]);
      setModalityOptions(modality.data);
      setSessionTypesOptions(sessionTypes.data);
      setAvailabilityOptions(availability.data);
    } catch (err) {
      console.error('Error fetching lookup data:', err);
      setError('Failed to load lookup data');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCounsellor(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleMultiSelectChange = (
    event: SelectChangeEvent<string[]>,
    field: keyof Counsellor
  ) => {
    const value = event.target.value;
    setCounsellor(prev => ({
      ...prev,
      [field]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/api/counsellors/profile', { ...counsellor, experience });
      setSaving(false);
      onSaved();
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save changes');
      setSaving(false);
    }
  };


  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <StyledDialogTitle>
        <Typography variant="h6" sx={{ fontFamily: 'Roboto' }}>
          Edit Profile
        </Typography>
      </StyledDialogTitle>
      <DialogContent>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={counsellor.firstName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={counsellor.lastName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={counsellor.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={counsellor.address || ''}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Qualifications"
                  name="qualifications"
                  value={counsellor.qualifications || ''}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Experience"
                  name="experience"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="modality-label">Modality</InputLabel>
                  <Select
                    labelId="modality-label"
                    multiple
                    value={counsellor.modality}
                    onChange={(e) => handleMultiSelectChange(e as SelectChangeEvent<string[]>, 'modality')}
                    input={<OutlinedInput label="Modality" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={modalityOptions.find(opt => opt.value === value)?.displayName || value} 
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {modalityOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        style={getStyles(option.value, counsellor.modality, theme)}
                      >
                        {option.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="session-types-label">Session Types</InputLabel>
                  <Select
                    labelId="session-types-label"
                    multiple
                    value={counsellor.sessionTypes}
                    onChange={(e) => handleMultiSelectChange(e as SelectChangeEvent<string[]>, 'sessionTypes')}
                    input={<OutlinedInput label="Session Types" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={sessionTypesOptions.find(opt => opt.value === value)?.displayName || value} 
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {sessionTypesOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        style={getStyles(option.value, counsellor.sessionTypes, theme)}
                      >
                        {option.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="availability-label">Availability</InputLabel>
                  <Select
                    labelId="availability-label"
                    multiple
                    value={counsellor.availability}
                    onChange={(e) => handleMultiSelectChange(e as SelectChangeEvent<string[]>, 'availability')}
                    input={<OutlinedInput label="Availability" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={availabilityOptions.find(opt => opt.value === value)?.displayName || value} 
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {availabilityOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        style={getStyles(option.value, counsellor.availability, theme)}
                      >
                        {option.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button onClick={onClose} disabled={saving}>
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileEdit;
